import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  SignOut, Trash, Phone, EnvelopeSimple, MapPin, Tray, ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/site/Logo";

const STATUS = {
  new: { label: "New", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  contacted: { label: "Contacted", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  scheduled: { label: "Scheduled", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  closed: { label: "Closed", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};
const STATUS_KEYS = Object.keys(STATUS);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, scheduled: 0, closed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([api.get("/admin/quotes"), api.get("/admin/stats")]);
      setQuotes(q.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/quotes/${id}`);
      toast.success("Quote deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const doLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  const visible = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen bg-muted/40" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center">
              <LogoMark className="w-6 h-6" gradient />
            </div>
            <div>
              <p className="font-heading text-lg leading-none">Explus Cleaning</p>
              <p className="text-xs text-white/60 mt-0.5">Quote Requests</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/70">{user?.email}</span>
            <button onClick={load} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Refresh" data-testid="refresh-btn">
              <ArrowsClockwise size={20} />
            </button>
            <button onClick={doLogout} className="flex items-center gap-2 text-sm rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 transition-colors" data-testid="logout-btn">
              <SignOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Requests" value={stats.total} testid="stat-total" />
          <StatCard label="New" value={stats.new} accent="text-emerald-600" testid="stat-new" />
          <StatCard label="Scheduled" value={stats.scheduled} accent="text-blue-600" testid="stat-scheduled" />
          <StatCard label="Closed" value={stats.closed} accent="text-slate-500" testid="stat-closed" />
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", ...STATUS_KEYS].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-sm font-medium rounded-full px-4 py-2 border transition-colors ${
                filter === f ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground/70 border-border hover:border-primary/40"
              }`}
              data-testid={`filter-${f}`}
            >
              {f === "all" ? "All" : STATUS[f].label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3" data-testid="empty-state">
            <Tray size={48} weight="duotone" />
            <p>No quote requests {filter !== "all" ? `with status "${STATUS[filter].label}"` : "yet"}.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {visible.map((q) => (
              <div key={q.id} className="bg-card border border-border rounded-2xl p-5 md:p-6" data-testid={`quote-card-${q.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-xl text-primary">{q.name}</h3>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS[q.status]?.cls || STATUS.new.cls}`}>
                        {STATUS[q.status]?.label || q.status}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-foreground/70">
                      <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone size={16} weight="duotone" /> {q.phone}</a>
                      <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-primary break-all"><EnvelopeSimple size={16} weight="duotone" /> {q.email}</a>
                      {q.address && <span className="flex items-center gap-1.5"><MapPin size={16} weight="duotone" /> {q.address}</span>}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-md bg-secondary px-2.5 py-1 font-medium text-primary">{q.service}</span>
                      {q.frequency && <span className="rounded-md bg-muted px-2.5 py-1 font-medium text-foreground/70">{q.frequency}</span>}
                    </div>
                    <p className="mt-3 text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{q.message}</p>
                    <p className="mt-3 text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Select value={q.status} onValueChange={(v) => changeStatus(q.id, v)}>
                      <SelectTrigger className="w-36 h-10" data-testid={`status-select-${q.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="p-2.5 rounded-lg text-destructive hover:bg-destructive/10 transition-colors" data-testid={`delete-btn-${q.id}`}>
                          <Trash size={20} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this quote request?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently remove the request from {q.name}. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(q.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-${q.id}`}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, accent = "text-primary", testid }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid={testid}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={`font-heading text-3xl mt-1 ${accent}`}>{value}</p>
    </div>
  );
}
